import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import SegmentedControl from 'components/shared/SegmentedControl/SegmentedControl';
import Hint from 'components/shared/shared/Hint';

import DrawSignature from './components/DrawSignature';
import TypeSignature from './components/TypeSignature';
import TypefaceDropdown from './components/TypefaceDropdown';
import { useContainerWidth } from './hooks/useContainerWidth';
import { SIGNATURE_HINT, SIGNATURE_TABS, TYPE_SIGNATURE_FONTS } from './lib/constants';
import { loadSignatureFonts } from './lib/loadSignatureFonts';

/**
 * @param {import('./lib/propTypes').SignatureProps} props
 */
const Signature = ({
  value,
  onChange,
  label = 'Your signature',
  penColor = '#000000',
  canvasHeight = 200,
  hint = true,
  defaultName = '',
  className = '',
  'data-qa': dataQa = ''
}) => {
  const [mode, setMode] = useState('draw');
  const [fontId, setFontId] = useState(TYPE_SIGNATURE_FONTS[0].id);
  const [fontsReady, setFontsReady] = useState(false);
  const [drawHistory, setDrawHistory] = useState({ canUndo: false, canRedo: false });
  /** @type {React.MutableRefObject<{ getValue: () => string | null, clear: () => void, undo: () => string | null, redo: () => string | null } | null>} */
  const drawRef = useRef(null);
  /** @type {React.MutableRefObject<{ getValue: () => string | null, clear: () => void } | null>} */
  const typeRef = useRef(null);
  const [canvasAreaRef, containerWidth] = useContainerWidth();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;
    loadSignatureFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const emitChange = useCallback((next) => onChangeRef.current?.(next), []);

  const handleTabChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const nextValue =
      nextMode === 'draw' ? drawRef.current?.getValue?.() : typeRef.current?.getValue?.();
    emitChange(nextValue || null);
  };

  const handleUndo = () => {
    if (mode !== 'draw' || !drawRef.current?.undo) return;
    emitChange(drawRef.current.undo());
  };

  const handleRedo = () => {
    if (mode !== 'draw' || !drawRef.current?.redo) return;
    emitChange(drawRef.current.redo());
  };

  const handleClear = () => {
    if (mode === 'draw') {
      drawRef.current?.clear?.();
    } else {
      typeRef.current?.clear?.();
    }
    emitChange(null);
  };

  const canUndo = mode === 'draw' && drawHistory.canUndo;
  const canRedo = mode === 'draw' && drawHistory.canRedo;

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <SegmentedControl
        items={SIGNATURE_TABS}
        active={mode}
        disableOverflow
        onClick={(item) => handleTabChange(item.value)}
      />

      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-500 text-neutral-800">{label}</span>
        {mode === 'draw' ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Button
              variant="link"
              size="small"
              color="neutral"
              icon="undo-mono"
              tooltip="Undo"
              aria-label="Undo signature stroke"
              onClick={handleUndo}
              isDisabled={!canUndo}
            />
            <Button
              variant="link"
              size="small"
              color="neutral"
              icon="redo-mono"
              tooltip="Redo"
              aria-label="Redo signature stroke"
              onClick={handleRedo}
              isDisabled={!canRedo}
            />
            <Button
              variant="link"
              size="small"
              color="neutral"
              text="Clear"
              onClick={handleClear}
            />
          </div>
        ) : (
          <div className="flex items-center gap-1 text-sm text-neutral-600">
            <span>Typeface:</span>
            <TypefaceDropdown fontId={fontId} onChange={setFontId} />
          </div>
        )}
      </div>

      <div
        ref={canvasAreaRef}
        style={{ height: canvasHeight }}
        className="relative w-full min-w-0 overflow-hidden">
        <div className={mode === 'draw' ? 'block' : 'hidden'}>
          <DrawSignature
            active={mode === 'draw'}
            value={mode === 'draw' ? value : null}
            onChange={emitChange}
            onHistoryChange={setDrawHistory}
            penColor={penColor}
            canvasHeight={canvasHeight}
            containerWidth={containerWidth}
            handleRef={drawRef}
            data-qa={dataQa}
          />
        </div>
        <div className={mode === 'type' ? 'block' : 'hidden'}>
          <TypeSignature
            active={mode === 'type'}
            onChange={emitChange}
            canvasHeight={canvasHeight}
            containerWidth={containerWidth}
            fontId={fontId}
            fontsReady={fontsReady}
            defaultName={defaultName}
            handleRef={typeRef}
            data-qa={dataQa}
          />
        </div>
      </div>

      {hint && <Hint hint={SIGNATURE_HINT} />}
    </div>
  );
};

export default Signature;
